import React, { useEffect, useState, useContext } from 'react';
import { Autocomplete } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Grid, Paper, Box, TextField, Button, Typography,
  FormControlLabel, RadioGroup, Radio, Avatar,
  InputLabel, Select, MenuItem, FormControl,
  Checkbox, FormGroup, useTheme, useMediaQuery,
} from '@mui/material';
import axios from 'axios';

const supportOptions = [
  "Can refer in my company",
  "Can hire directly talent from community",
  "Can educate or mentor people looking for job",
  "Can get you connected with a Leader for further help",
  "Can train & hire college graduates",
  "Can help mentoring for new business",
  "Can help mentoring young talents for their future career",
  "Currently not in a position to help"
];

const supporter_lst_option = [
  "Are you an IT leader?",
  "Are you an SME?",
  "Are you a business owner?"
];

const ProfileUpdate = () => {
  const { setProfileUpdated } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    intent: '',
    firstName: '',
    lastName: '',
    profilePhoto: '',
    industry: '',
    company: '',
    role: '',
    mobile: '',
    anonymousPhoto: false,
    anonymousMobile: false,
    isprovider: false,
    helpfields: [],
    supporter_lst: [],
    other_help: '',
    other_support: '',
    localcommunity: '',
  });
  const [preview, setPreview] = useState('');
  const [options, setOptions] = useState({
    industries: [],
    companies: [],
    roles: [],
  });
  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (token) {
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/profile/get`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => {
          const data = res.data;
          setForm({
            intent: data.intent || '',
            other_help: data.other_help || '',
            other_support: data.other_support || '',
            localcommunity: data.localcommunity || '',
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            profilePhoto: data.profile_photo || '',
            industry: data.industry || '',
            company: data.company || '',
            role: data.role || '',
            mobile: data.mobile || '',
            anonymousPhoto: !!data.anonymous_photo,
            anonymousMobile: !!data.anonymous_mobile,
            isprovider: !!data.isprovider,
            helpfields: Array.isArray(data.helpfields) ? data.helpfields : (data.helpfields ? JSON.parse(data.helpfields) : []),
            supporter_lst: Array.isArray(data.supporter_lst) ? data.supporter_lst : (data.supporter_lst ? JSON.parse(data.supporter_lst) : [])
          });
          setPreview(data.profile_photo || '');
        })
        .catch(err => {
          console.error('Error fetching profile:', err);
        });
    }

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/profile/options`)
      .then(res => setOptions(res.data))
      .catch(err => console.error('Error fetching options:', err));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, profilePhoto: reader.result }));
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/profile/update`, form, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    })
      .then(() => {
        alert('Profile updated successfully');
        setProfileUpdated(true);
        navigate(`/home`);
      })
      .catch(err => {
        console.error('Error updating profile:', err);
        alert('Failed to update profile');
      });
  };

  const handleSupportChange = (option) => {
    setForm((prev) => {
      const current = prev.helpfields.includes(option);
      return {
        ...prev,
        helpfields: current
          ? prev.helpfields.filter(o => o !== option)
          : [...prev.helpfields, option],
      };
    });
  };

  const supporter_lst_change = (option) => {
    setForm((prev) => {
      const current = prev.supporter_lst.includes(option);
      return {
        ...prev,
        supporter_lst: current
          ? prev.supporter_lst.filter(o => o !== option)
          : [...prev.supporter_lst, option],
      };
    });
  };

  return (
    <Paper elevation={3} sx={{ p: 4, mt: 5, borderRadius: 3, bgcolor: '#f9f9f9' }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Complete Your Profile
      </Typography>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 } }}>
        <Grid container spacing={3}>
          {/* Left Column for Intent & Onboarding (Always visible on desktop) */}
          <Grid item size={isMobile ? 12 : 4} xs={isMobile ? 12 : 4} md={4}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Select Your Intent
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel id="intent-label">Intent</InputLabel>
                <Select
                  fullWidth
                  labelId="intent-label"
                  value={form.intent}
                  onChange={(e) => setForm(prev => ({ ...prev, intent: e.target.value }))}
                >
                  <MenuItem value="help">I want to help</MenuItem>
                  <MenuItem value="need">I need help</MenuItem>
                </Select>
              </FormControl>

              <Box mt={3}>
                {!form.intent && (
                  <Typography variant="body2" color="text.secondary">
                    Please select an intent above to continue.
                  </Typography>
                )}

                {/* Help Section */}
                <Box sx={{ display: form.intent === 'help' ? 'block' : 'none' }}>
                  <Typography variant="body1" gutterBottom>
                    Nice 😊 Love to get you added as our supporter if you have 2 mins
                  </Typography>
                  <FormGroup>
                    {supporter_lst_option.map((option) => (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            checked={form.supporter_lst.includes(option)}
                            onChange={() => supporter_lst_change(option)}
                          />
                        }
                        label={option}
                      />
                    ))}
                    <TextField
                      fullWidth
                      label="Other"
                      value={form.other_support}
                      onChange={(e) => setForm(prev => ({ ...prev, other_support: e.target.value }))}
                      margin="dense"
                    />
                  </FormGroup>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Areas you want to support:
                  </Typography>
                  <FormGroup>
                    {supportOptions.map((option) => (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            checked={form.helpfields.includes(option)}
                            onChange={() => handleSupportChange(option)}
                          />
                        }
                        label={option}
                      />
                    ))}
                    <TextField
                      fullWidth
                      label="Other"
                      value={form.other_help}
                      onChange={(e) => setForm(prev => ({ ...prev, other_help: e.target.value }))}
                      margin="dense"
                    />
                  </FormGroup>

                  <TextField
                    fullWidth
                    label="Are you a member of any local community?"
                    value={form.localcommunity}
                    onChange={(e) => setForm(prev => ({ ...prev, localcommunity: e.target.value }))}
                    margin="normal"
                  />
                </Box>

                {/* Need Help Section */}
                <Box sx={{ display: form.intent === 'need' ? 'block' : 'none' }}>
                  <Typography variant="body1" gutterBottom>
                    We’ll help you get started by updating your profile.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    label="What help do you need?"
                    value={form.other_help}
                    onChange={(e) => setForm(prev => ({ ...prev, other_help: e.target.value }))}
                    margin="normal"
                    rows={3}
                  />
                  <TextField
                    fullWidth
                    label="Are you a member of any local community?"
                    value={form.localcommunity}
                    onChange={(e) => setForm(prev => ({ ...prev, localcommunity: e.target.value }))}
                    margin="normal"
                  />
                </Box>
              </Box>
              {isMobile && activeStep === 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" color="primary" onClick={handleNext}>
                    Next
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Right Column for Profile Update */}
          <Grid item size={isMobile ? 12 : 6} xs={isMobile ? 12 : 6} md={8} sx={{ display: isMobile && activeStep !== 1 && 'none' }}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Basic Profile Information
              </Typography>

              {/* Profile Image Upload */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  src={preview}
                  alt="Profile Preview"
                  sx={{ width: 80, height: 80, mr: 2 }}
                />
                <Button variant="contained" component="label">
                  Upload Photo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.anonymousPhoto}
                    onChange={handleChange}
                    name="anonymousPhoto"
                  />
                }
                label="Make my photo anonymous"
              />

              {/* Name and Mobile Fields */}
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                margin="normal"
              />
               <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle1">Are you a Provider?</Typography>
                  <RadioGroup
                      row
                      name="isprovider"
                      value={form.isprovider ? 'yes' : 'no'}
                      onChange={(e) => {
                      setForm(prev => ({
                          ...prev,
                          isprovider: e.target.value === 'yes'
                      }));  
                      }}
                  >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup> 
              </Box> 
              <TextField
                fullWidth
                label="Mobile Number"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                margin="normal"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.anonymousMobile}
                    onChange={handleChange}
                    name="anonymousMobile"
                  />
                }
                label="Make my mobile number anonymous"
              />

              {/* Industry, Company, Role Autocompletes */}
              <Autocomplete
               freeSolo
                fullWidth
                options={options.industries}
                value={form.industry}
                onChange={(e, newVal) => setForm(prev => ({ ...prev, industry: newVal || '' }))}
                onInputChange={(e, newInputValue) => {
                  setForm(prev => ({ ...prev, industry: newInputValue }));
                }}
                renderInput={(params) => <TextField {...params} label="Industry" margin="normal" />}
              />
              <Autocomplete
                freeSolo
                fullWidth
                options={options.companies}
                value={form.company}
                onChange={(e, newVal) => setForm(prev => ({ ...prev, company: newVal || '' }))}
                onInputChange={(e, newInputValue) => {
                  setForm(prev => ({ ...prev, company: newInputValue }));
                }}
                renderInput={(params) => <TextField {...params} label="Company" margin="normal" />}
              />
              <Autocomplete
                freeSolo
                fullWidth
                options={options.roles}
                value={form.role}
                onChange={(e, newVal) => setForm(prev => ({ ...prev, role: newVal || '' }))}
                onInputChange={(e, newInputValue) => {
                  setForm(prev => ({ ...prev, role: newInputValue }));
                }}
                renderInput={(params) => <TextField {...params} label="Role" margin="normal" />}
              />
              {isMobile && activeStep === 1 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Button onClick={handleBack}>Back</Button>
                  <Button variant="contained" color="primary" onClick={handleSubmit}>
                    Save Profile
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Desktop Submit Button */}
          {!isMobile && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSubmit}
                >
                  Save Profile
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Paper>
  );
};

export default ProfileUpdate;
